import { useEffect, useRef, useMemo, useState } from "react"
import { useInngestSubscription } from "@inngest/realtime/hooks"
import { Realtime } from "@inngest/realtime"
import { useAuthorizedQuery } from "@/api"
import { params } from "@/params";

export interface ReplyProgressUpdate {
  status:
    | "pending"
    | "browser-starting"
    | "browser-running"
    | "browser-completed"
    | "generating"
    | "completed"
    | "error"
  message: string
  toolName?: string
  recordingUrl?: string
}

export interface ReplyCompletedUpdate {
  text: string
  toolResults: Array<{
    toolName: string
    result: unknown
    args: Record<string, unknown>
    recordingUrl?: string
  }>
  chainOfThoughtSteps: unknown[]
  contextExport: unknown[]
}

export interface ReplyErrorUpdate {
  error: string
  status: "error" | "timeout" | "rejected"
  reason?: string
}

export interface AsyncReplyState {
  isSubscribing: boolean
  isCompleted: boolean
  isError: boolean
  progress: ReplyProgressUpdate | null
  completedResponse: ReplyCompletedUpdate | null
  error: ReplyErrorUpdate | null
}

const initialState: AsyncReplyState = {
  isSubscribing: false,
  isCompleted: false,
  isError: false,
  progress: null,
  completedResponse: null,
  error: null,
}

export interface AsyncReplySubscriptionProps {
  requestId: string
  onCompleted?: (response: ReplyCompletedUpdate) => void
  onError?: (error: ReplyErrorUpdate) => void
  onProgress?: (progress: ReplyProgressUpdate) => void
  timeout?: number
}

const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000

interface RealtimeMessage {
  topic: "progress" | "completed" | "error"
  data: unknown
}

export function useAsyncReplySubscription({
  requestId,
  onCompleted,
  onError,
  onProgress,
  timeout = DEFAULT_TIMEOUT_MS,
}: AsyncReplySubscriptionProps): AsyncReplyState {
  const [isTimedOut, setIsTimedOut] = useState(false)
  const lastActivityRef = useRef(Date.now())

  // Fetch token using query - no useEffect needed
  const { data: tokenData, error: tokenError } = useAuthorizedQuery<{ token: string }>({
    url: `${params.API_URL}/platform/vercel-proxy/api/realtime/subscribe`,
    queryKey: `inngest-token-${requestId}`,
    method: "POST",
    body: { type: "reply", requestId },
    enabled: !!requestId,
    cacheTime: Infinity,
  })

  const token = tokenData?.token

  // Derive terminal state to disable subscription
  const messages = useRef<RealtimeMessage[]>([])

  // Reset messages and timeout when requestId changes
  useEffect(() => {
    messages.current = []
    setIsTimedOut(false)
  }, [requestId])

  // Check if we have terminal state to disable subscription
  const hasCompleted = messages.current.some(m => m.topic === "completed")
  const hasError = messages.current.some(m => m.topic === "error")

  const subscription = useInngestSubscription({
    token: token as unknown as Realtime.Subscribe.Token,
    enabled: !!token && !isTimedOut && !hasCompleted && !hasError,
    key: requestId,
  })

  // Update messages ref when subscription data changes
  if (subscription.data?.length) {
    messages.current = subscription.data as RealtimeMessage[]
  }

  // Derive state from subscription data - no useEffect needed
  const state = useMemo((): AsyncReplyState => {
    // Handle token fetch error
    if (tokenError) {
      return {
        ...initialState,
        isError: true,
        error: { status: "error", error: String(tokenError) },
      }
    }

    // Handle timeout
    if (isTimedOut) {
      return {
        ...initialState,
        isError: true,
        error: { status: "timeout", error: "Request timed out - no response received from server" },
      }
    }

    // Handle subscription error
    if (subscription.error) {
      return {
        ...initialState,
        isError: true,
        error: { status: "error", error: subscription.error.message },
      }
    }

    const msgs = messages.current
    if (!msgs.length) {
      return {
        ...initialState,
        isSubscribing: subscription.state === "active",
      }
    }

    // Find terminal and progress messages
    const completedMsg = msgs.find(m => m.topic === "completed")
    const errorMsg = msgs.find(m => m.topic === "error")
    const progressMsgs = msgs.filter(m => m.topic === "progress")
    const latestProgress = progressMsgs[progressMsgs.length - 1]?.data as ReplyProgressUpdate | undefined

    if (completedMsg) {
      return {
        isSubscribing: false,
        isCompleted: true,
        isError: false,
        completedResponse: completedMsg.data as ReplyCompletedUpdate,
        progress: latestProgress ?? null,
        error: null,
      }
    }

    if (errorMsg) {
      return {
        isSubscribing: false,
        isCompleted: false,
        isError: true,
        completedResponse: null,
        progress: latestProgress ?? null,
        error: errorMsg.data as ReplyErrorUpdate,
      }
    }

    return {
      isSubscribing: subscription.state === "active",
      isCompleted: false,
      isError: false,
      completedResponse: null,
      progress: latestProgress ?? null,
      error: null,
    }
  }, [subscription.data, subscription.state, subscription.error, tokenError, isTimedOut])

  // Callbacks - compare previous state to detect transitions
  const prevStateRef = useRef<AsyncReplyState>(initialState)
  useEffect(() => {
    const prev = prevStateRef.current
    prevStateRef.current = state

    if (state.isCompleted && !prev.isCompleted && state.completedResponse) {
      onCompleted?.(state.completedResponse)
    }
    if (state.isError && !prev.isError && state.error) {
      onError?.(state.error)
    }
    if (state.progress && state.progress !== prev.progress) {
      lastActivityRef.current = Date.now()
      onProgress?.(state.progress)
    }
    if (state.isSubscribing && !prev.isSubscribing) {
      lastActivityRef.current = Date.now()
    }
  }, [state, onCompleted, onError, onProgress])

  // Timeout handling
  useEffect(() => {
    if (!state.isSubscribing || state.isCompleted || state.isError) return

    const intervalId = setInterval(() => {
      if (Date.now() - lastActivityRef.current >= timeout) {
        setIsTimedOut(true)
      }
    }, 10000)

    return () => clearInterval(intervalId)
  }, [state.isSubscribing, state.isCompleted, state.isError, timeout])

  return state
}
